import { Cartridge, Mirroring } from '../interfaces/cartridge';
import { RomImage } from './mappers/base';
import { NromCartridge } from './mappers/nrom';
import { Mmc1Cartridge } from './mappers/mmc1';
import { UxromCartridge } from './mappers/uxrom';
import { CnromCartridge } from './mappers/cnrom';
import { Mmc3Cartridge } from './mappers/mmc3';

export { NromCartridge } from './mappers/nrom';
export { Mmc1Cartridge } from './mappers/mmc1';
export { UxromCartridge } from './mappers/uxrom';
export { CnromCartridge } from './mappers/cnrom';
export { Mmc3Cartridge } from './mappers/mmc3';
export { RomImage } from './mappers/base';

/** Decode a NES 2.0 RAM shift-count byte nibble (64 << n bytes, 0 = none). */
function ramShiftSize(nibble: number): number {
    return nibble === 0 ? 0 : 64 << nibble;
}

/**
 * Parse an iNES / NES 2.0 ROM image into a RomImage.
 *
 * Header layout: bytes 0-3 magic "NES\x1A"; 4 = PRG 16KB units; 5 = CHR 8KB
 * units; 6 = flags6 (mirroring/battery/trainer/four-screen + mapper low
 * nibble); 7 = flags7 (mapper mid nibble; (flags7 & 0x0C) === 0x08 marks
 * NES 2.0). NES 2.0 adds: byte 8 = mapper high nibble + submapper, byte 9 =
 * PRG/CHR size MSBs, byte 10/11 = PRG/CHR RAM shift counts.
 */
export function parseRom(romBytes: Uint8Array): RomImage {
    if (romBytes.length < 16 ||
        romBytes[0] !== 0x4E || romBytes[1] !== 0x45 || romBytes[2] !== 0x53 || romBytes[3] !== 0x1A) {
        throw new Error('Not an iNES ROM (bad magic)');
    }

    const flags6 = romBytes[6];
    const flags7 = romBytes[7];
    const isNes2 = (flags7 & 0x0C) === 0x08;

    let mapper = (flags6 >> 4) | (flags7 & 0xF0);
    let submapper = 0;
    let prgUnits = romBytes[4];
    let chrUnits = romBytes[5];
    let prgRamSize = 0;
    let chrRamSize = 0x2000; // default 8KB CHR-RAM when no CHR ROM

    if (isNes2) {
        mapper |= (romBytes[8] & 0x0F) << 8;
        submapper = romBytes[8] >> 4;
        prgUnits |= (romBytes[9] & 0x0F) << 8;
        chrUnits |= (romBytes[9] & 0xF0) << 4;
        // Exponent-multiplier size encoding (nibble 0xF) not supported.
        prgRamSize = ramShiftSize(romBytes[10] & 0x0F);
        chrRamSize = ramShiftSize(romBytes[11] & 0x0F) || 0x2000;
    } else {
        // iNES byte 8 = PRG-RAM in 8KB units (unreliable in the wild).
        prgRamSize = (romBytes[8] || 0) * 0x2000;
    }

    const battery = (flags6 & 0x02) !== 0;
    if (prgRamSize === 0 && battery) {
        prgRamSize = 0x2000; // battery-backed games get a RAM window
    }

    const mirroring: Mirroring = (flags6 & 0x08) ? 'four-screen'
        : (flags6 & 0x01) ? 'vertical' : 'horizontal';
    const hasTrainer = (flags6 & 0x04) !== 0;

    const prgSize = prgUnits * 0x4000;
    const chrSize = chrUnits * 0x2000;
    let offset = 16 + (hasTrainer ? 512 : 0);
    if (romBytes.length < offset + prgSize) {
        throw new Error(`Truncated ROM: need ${offset + prgSize} bytes, have ${romBytes.length}`);
    }
    const prgRom = romBytes.slice(offset, offset + prgSize);
    offset += prgSize;
    const chrRom = chrSize ? romBytes.slice(offset, offset + chrSize) : null;

    return { prgRom, chrRom, chrRamSize, prgRamSize, mirroring, battery, mapper, submapper, isNes2 };
}

/**
 * Build the cartridge for a ROM image. Supported mappers:
 * 0 NROM, 1 MMC1, 2 UxROM, 3 CNROM, 4 MMC3.
 */
export function createCartridge(romBytes: Uint8Array): Cartridge {
    const image = parseRom(romBytes);

    // Mappers 1-4 commonly ship with an 8KB PRG-RAM window even when the
    // header says nothing (iNES byte 8 is unreliable).
    if (image.prgRamSize === 0 && image.mapper !== 0) {
        image.prgRamSize = 0x2000;
    }

    switch (image.mapper) {
        case 0: return new NromCartridge(image);
        case 1: return new Mmc1Cartridge(image);
        case 2: return new UxromCartridge(image);
        case 3: return new CnromCartridge(image);
        case 4: return new Mmc3Cartridge(image);
        default:
            throw new Error(`Unsupported mapper ${image.mapper} (supported: 0 NROM, 1 MMC1, 2 UxROM, 3 CNROM, 4 MMC3)`);
    }
}
